import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { API } from "../config";
import {
  MapPin, Building2, TrendingDown, Target, ArrowRight,
  Sparkles, AlertTriangle, CheckCircle2, Flag,
} from "lucide-react";
import NotFoundPage from "./NotFoundPage";
import { trackEvent } from "../lib/analytics";

/**
 * V231.3 — Programmatic SEO landing: /mesto/:slug/ai-seo-scan
 *
 * One route handles 153 SK+CZ cities. Each render pulls deterministic
 * unique content from /api/city-pages/{slug} and injects:
 *   • <title>, <meta description>, <link canonical>
 *   • Schema.org LocalBusiness + BreadcrumbList JSON-LD
 *   • 5 industry sections with city-specific example firm names
 *
 * CTA funnels to /snapshot (€7 tripwire) with pre-filled city context.
 */
export default function CityAiSeoPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setError(false);
    setData(null);
    axios
      .get(`${API}/city-pages/${slug}`)
      .then((r) => {
        setData(r.data);
        trackEvent("view_city_ai_seo", {
          slug,
          country: r.data?.country,
          source: `city_${r.data?.country?.toLowerCase() || "sk"}`,
        });
      })
      .catch((err) => {
        if (err?.response?.status === 404 || !err?.response) setError(true);
        else console.error(err);
      });
  }, [slug]);

  // V231.3 — Imperatively override the <link rel="canonical"> baked into
  // index.html so Google sees the city-specific URL. Helmet appends but
  // doesn't rewrite the first-match canonical the crawler reads.
  useEffect(() => {
    if (!data?.canonical_url) return;
    const el = document.querySelector('link[rel="canonical"]');
    const prev = el?.getAttribute("href");
    if (el) {
      el.setAttribute("href", data.canonical_url);
    }
    return () => {
      if (el && prev) el.setAttribute("href", prev);
    };
  }, [data?.canonical_url]);

  if (error) return <NotFoundPage />;
  if (!data) {
    return (
      <div className="min-h-screen bg-[#0A0F2E] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const m = data.metrics;
  const title = `AI SEO scan ${data.name} — ${m.visibility_pct}% viditeľnosť v ChatGPT, Perplexity, Google AI`;
  const description = `Zisti ako ${data.name} vyzerá v AI vyhľadávačoch (ChatGPT, Perplexity, Claude, Gemini). ${m.local_competitors} lokálnych firiem · priemerná AI viditeľnosť ${m.visibility_pct}% · citation gap ${m.citation_gap_pct}%. Spusti bezplatný AI SEO scan pre tvoj biznis.`;

  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Domov", item: "https://toprankpulse.com/" },
      { "@type": "ListItem", position: 2, name: `AI SEO Mestá`, item: "https://toprankpulse.com/mesto" },
      { "@type": "ListItem", position: 3, name: data.name, item: data.canonical_url },
    ],
  };

  const localBusiness = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `TopRankPulse — AI SEO scan ${data.name}`,
    description,
    url: data.canonical_url,
    areaServed: { "@type": "City", name: data.name, address: { "@type": "PostalAddress", addressCountry: data.country } },
    priceRange: "€",
    aggregateRating: { "@type": "AggregateRating", ratingValue: "4.8", reviewCount: "120" },
  };

  return (
    <main className="min-h-screen bg-[#0A0F2E] text-white" data-testid="city-ai-seo-page">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={data.canonical_url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={data.canonical_url} />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbs)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusiness)}</script>
      </Helmet>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 pt-16 pb-10" data-testid="city-hero">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-amber-400 font-semibold mb-4">
          <Flag className="w-3.5 h-3.5" />
          {data.country === "SK" ? "Slovensko" : "Česko"} · {data.region}
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-5">
          AI SEO scan <span className="text-amber-300">{data.name}</span>
        </h1>
        <p className="text-xl text-slate-300 leading-relaxed max-w-3xl mb-8">
          {data.pain_point}. Kým {data.name} rastie, {m.citation_gap_pct}% lokálnych firiem{" "}
          nemá <strong className="text-white">žiadnu AI SEO viditeľnosť</strong> v ChatGPT, Perplexity ani Google AI Overview.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/snapshot"
            data-testid="city-cta-snapshot"
            className="bg-amber-400 hover:bg-amber-300 text-black font-bold px-7 py-3.5 rounded-lg transition inline-flex items-center gap-2"
          >
            Spusti AI SEO scan pre {data.name} <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/benchmark"
            data-testid="city-cta-benchmark"
            className="border border-white/20 hover:border-white/40 text-white font-semibold px-7 py-3.5 rounded-lg transition"
          >
            Konkurenčný benchmark →
          </Link>
        </div>
      </section>

      {/* Metrics strip */}
      <section className="max-w-5xl mx-auto px-6 pb-12" data-testid="city-metrics">
        <div className="grid sm:grid-cols-4 gap-3">
          <MetricCard icon={TrendingDown} label="AI SEO Score (priemer mesta)" value={`${m.ai_seo_score}/100`} tone="rose" />
          <MetricCard icon={Target} label="AI viditeľnosť" value={`${m.visibility_pct}%`} tone="amber" />
          <MetricCard icon={Building2} label="Lokálne firmy" value={m.local_competitors.toLocaleString("sk-SK")} tone="slate" />
          <MetricCard icon={AlertTriangle} label="Citation gap" value={`${m.citation_gap_pct}%`} tone="rose" />
        </div>
      </section>

      {/* Industries */}
      <section className="max-w-5xl mx-auto px-6 py-10" data-testid="city-industries">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-slate-400 mb-5">
          Top 5 odvetví v {data.name} s najväčším AI SEO potenciálom
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {data.featured_industries.map((ind, i) => (
            <div
              key={i}
              className="bg-[#111634] border border-slate-700/60 rounded-xl p-4 hover:border-amber-400/40 transition"
              data-testid={`industry-${i}`}
            >
              <div className="w-8 h-8 rounded-lg bg-amber-400/15 flex items-center justify-center mb-3">
                <Sparkles className="w-4 h-4 text-amber-400" />
              </div>
              <h3 className="text-sm font-semibold capitalize">{ind}</h3>
              <p className="text-xs text-slate-400 mt-1">
                {data.name}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* V231.5 — Real firms in this city (ORSF-indexed) with links
          to /firma/{ico}. Falls back to synthetic examples when the
          city isn't yet in city_firms_index. */}
      {data.real_firms && data.real_firms.length > 0 ? (
        <section className="max-w-5xl mx-auto px-6 py-10" data-testid="city-real-firms">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-slate-400 mb-5">
            Reálne top firmy registrované v {data.name}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.real_firms.map((f) => (
              <Link
                key={f.ico}
                to={`/firma/${f.ico}/ai-seo-scan`}
                data-testid={`real-firm-${f.ico}`}
                className="bg-[#111634] border border-slate-700/60 rounded-xl p-5 hover:border-amber-400/50 hover:bg-[#161c40] transition group"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="text-sm font-semibold text-white line-clamp-2">
                    {f.name}
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-amber-400 flex-shrink-0 mt-1" />
                </div>
                {f.domain && (
                  <div className="text-xs text-amber-300/80 font-mono mb-1">
                    {f.domain}
                  </div>
                )}
                <div className="text-[11px] text-slate-500">IČO {f.ico}</div>
              </Link>
            ))}
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Klikni na firmu → zobrazí sa jej AI SEO scorecard (ChatGPT citations, Perplexity, Gemini).
          </p>
        </section>
      ) : (
        <section className="max-w-5xl mx-auto px-6 py-10" data-testid="city-examples">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-slate-400 mb-5">
            Príkladové firmy v {data.name} — AI viditeľnosť vs. benchmark
          </h2>
          <div className="bg-[#111634] border border-slate-800 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[520px]">
                <thead>
                  <tr className="border-b border-slate-800 text-xs uppercase tracking-wider text-slate-500">
                    <th className="text-left py-3 px-4">Firma</th>
                    <th className="text-right py-3 px-4">AI viditeľnosť</th>
                    <th className="text-right py-3 px-4">Benchmark odvetvie</th>
                    <th className="text-right py-3 px-4">Stav</th>
                  </tr>
                </thead>
                <tbody>
                  {data.example_firms.map((f, i) => {
                    const gap = f.benchmark - f.visibility;
                    return (
                      <tr key={i} className="border-b border-slate-900 last:border-0">
                        <td className="py-3 px-4 font-semibold">{f.name}</td>
                        <td className="py-3 px-4 text-right font-mono">{f.visibility}%</td>
                        <td className="py-3 px-4 text-right text-slate-400">{f.benchmark}%</td>
                        <td className="py-3 px-4 text-right">
                          {gap > 25 ? (
                            <span className="text-rose-400 text-xs font-semibold">-{gap}%</span>
                          ) : gap > 5 ? (
                            <span className="text-amber-400 text-xs font-semibold">-{gap}%</span>
                          ) : (
                            <CheckCircle2 className="w-4 h-4 text-emerald-400 inline" />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            * Dáta sú ilustratívne na základe lokálnych priemerov pre {data.name}. Reálny scan pre tvoju doménu dostaneš cez €7 Snapshot.
          </p>
        </section>
      )}

      {/* Closing CTA */}
      <section className="max-w-3xl mx-auto px-6 py-16 text-center" data-testid="city-closing-cta">
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-5">
          Zisti presne, kde tvoj biznis v {data.name} <span className="text-amber-300">stráca AI SEO body</span>.
        </h2>
        <p className="text-slate-300 mb-7 leading-relaxed">
          €7 Snapshot ti za 90 sekúnd ukáže v ktorých AI vyhľadávačoch tvoja doména chýba a čo konkurencia robí lepšie.
        </p>
        <Link
          to="/snapshot"
          data-testid="city-closing-snapshot"
          className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-black font-bold px-8 py-4 rounded-lg transition"
        >
          Spusti scan pre {data.name} za €7 <ArrowRight className="w-4 h-4" />
        </Link>
        <div className="mt-5 flex items-center justify-center gap-2 text-xs text-slate-500">
          <MapPin className="w-3.5 h-3.5" />
          Pokrývame 153 miest v SR a ČR · Výsledok za 90s
        </div>
      </section>
    </main>
  );
}

function MetricCard({ icon: Icon, label, value, tone }) {
  const toneMap = {
    rose: "text-rose-400 border-rose-500/20",
    amber: "text-amber-400 border-amber-500/20",
    slate: "text-slate-200 border-slate-700",
  };
  return (
    <div className={`bg-[#111634] border rounded-xl p-4 ${toneMap[tone] || toneMap.slate}`}>
      <Icon className="w-4 h-4 mb-2" />
      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{label}</div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}
